'use client'

import React from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import type { WorkflowStep } from '@/types/synoptique'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Copy,
  Edit2,
  Files,
  GripVertical,
  Plus,
  Trash2,
} from 'lucide-react'

interface PageData {
  id: string
  name: string
  [key: string]: unknown
}

interface ClipboardData {
  elements: { id: string }[]
}

interface ViewBox {
  x: number
  y: number
  width: number
  height: number
}

interface PageTabsToolbarProps {
  currentStep: WorkflowStep
  isDataMode: boolean
  isPowerMode: boolean
  isExportMode: boolean
  exportViewMode: 'data' | 'power'
  setExportViewMode: (v: 'data' | 'power') => void
  // Data pages
  dataPages: PageData[]
  currentDataPageId: string
  setCurrentDataPageId: (id: string) => void
  addDataPage: () => void
  deleteDataPage: (id: string) => void
  duplicateDataPage: (id: string) => void
  moveDataPage: (from: number, to: number) => void
  // Power pages
  powerPages: PageData[]
  currentPowerPageId: string
  setCurrentPowerPageId: (id: string) => void
  setCurrentStep: (step: WorkflowStep) => void
  addPowerPage: () => void
  deletePowerPage: (id: string) => void
  duplicatePowerPage: (id: string) => void
  movePowerPage: (from: number, to: number) => void
  // Tab drag
  draggedTabId: string | null
  setDraggedTabId: (id: string | null) => void
  // Page editing
  editingPageId: string | null
  setEditingPageId: (id: string | null) => void
  editingPageName: string
  setEditingPageName: (name: string) => void
  editingPageType: 'data' | 'power' | null
  startEditingPage: (id: string, type: 'data' | 'power') => void
  finishEditingPage: () => void
  // State
  readOnly: boolean
  viewBox: ViewBox
  selectedElements: string[]
  getEffectiveSelectionCount: () => number
  clipboard: ClipboardData | null
  pasteElements: () => void
}

export function PageTabsToolbar({
  currentStep,
  isDataMode,
  isPowerMode,
  isExportMode,
  exportViewMode,
  setExportViewMode,
  dataPages,
  currentDataPageId,
  setCurrentDataPageId,
  addDataPage,
  deleteDataPage,
  duplicateDataPage,
  moveDataPage,
  powerPages,
  currentPowerPageId,
  setCurrentPowerPageId,
  setCurrentStep,
  addPowerPage,
  deletePowerPage,
  duplicatePowerPage,
  movePowerPage,
  draggedTabId,
  setDraggedTabId,
  editingPageId,
  setEditingPageId,
  editingPageName,
  setEditingPageName,
  editingPageType,
  startEditingPage,
  finishEditingPage,
  readOnly,
  viewBox,
  selectedElements,
  getEffectiveSelectionCount,
  clipboard,
  pasteElements,
}: PageTabsToolbarProps) {
  const { t } = useLanguage()

  return (
    <div className="h-10 border-b bg-card/30 backdrop-blur-sm flex items-center px-4">
      {/* Indicateur de mode à gauche */}
      <div className="flex items-center space-x-2 min-w-[200px]">
        <div className={`w-2 h-2 rounded-full ${currentStep === 'data' ? 'bg-blue-400' :
            currentStep === 'cabling' ? 'bg-red-500' : 'bg-purple-400'
          }`}></div>
        <span className="text-sm font-medium">
          Mode {currentStep === 'data' ? 'Data' :
              currentStep === 'cabling' ? 'Power' : 'Export'}
        </span>
      </div>

      {/* Onglets au centre - Séparation Data / Power */}
      <div className="flex-1 flex items-center justify-center gap-4 px-4 overflow-x-auto">
        {/* Pages Data */}
        {(isDataMode || currentStep === 'export') && (
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-blue-500/10 text-blue-500 border-blue-500/30">Data</Badge>
            {dataPages.map((page, index) => (
              <div
                key={page.id}
                draggable
                onDragStart={(e) => {
                  setDraggedTabId(page.id)
                  if (e.dataTransfer) {
                    e.dataTransfer.effectAllowed = 'move'
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = 'move'
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (draggedTabId && draggedTabId !== page.id && draggedTabId.startsWith('data-')) {
                    const fromIndex = dataPages.findIndex(p => p.id === draggedTabId)
                    moveDataPage(fromIndex, index)
                  }
                  setDraggedTabId(null)
                }}
                onDragEnd={() => setDraggedTabId(null)}
                className={`
                  group relative flex items-center gap-1.5 px-3 py-1 rounded-t-md text-xs font-medium
                  transition-colors cursor-pointer
                  ${currentDataPageId === page.id && (isDataMode || (isExportMode && exportViewMode === 'data'))
                        ? 'bg-background text-foreground border-t border-x border-blue-500/30'
                        : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                  ${draggedTabId === page.id ? 'opacity-50' : ''}
                `}
              >
                <GripVertical className="h-3 w-3 opacity-0 group-hover:opacity-50 cursor-grab active:cursor-grabbing" />

                {editingPageId === page.id && editingPageType === 'data' ? (
                  <Input
                    autoFocus
                    value={editingPageName}
                    onChange={(e) => setEditingPageName(e.target.value)}
                    onBlur={finishEditingPage}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') finishEditingPage()
                      if (e.key === 'Escape') {
                        setEditingPageId(null)
                        setEditingPageName('')
                      }
                    }}
                    className="h-5 w-24 px-1 text-xs"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setCurrentDataPageId(page.id)
                      if (isExportMode) setExportViewMode('data')
                    }}
                    className="cursor-pointer"
                  >
                    {page.name}
                  </span>
                )}

                {!readOnly && (
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation()
                        startEditingPage(page.id, 'data')
                      }}
                      title="Renommer"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation()
                        duplicateDataPage(page.id)
                      }}
                      title="Dupliquer la page"
                    >
                      <Files className="h-3 w-3" />
                    </Button>

                    {dataPages.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm(`Supprimer "${page.name}" ?`)) {
                            deleteDataPage(page.id)
                          }
                        }}
                        title="Supprimer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {!readOnly && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 flex-shrink-0"
                onClick={addDataPage}
                title="Ajouter une page Data"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {/* Séparateur vertical entre Data et Power */}
        {currentStep === 'export' && (
          <Separator orientation="vertical" className="h-6" />
        )}

        {/* Pages Power */}
        {(isPowerMode || currentStep === 'export') && (
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-red-500/10 text-red-500 border-red-500/30">Power</Badge>
            {powerPages.map((page, index) => (
              <div
                key={page.id}
                draggable
                onDragStart={(e) => {
                  setDraggedTabId(page.id)
                  if (e.dataTransfer) {
                    e.dataTransfer.effectAllowed = 'move'
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  if (e.dataTransfer) {
                    e.dataTransfer.dropEffect = 'move'
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (draggedTabId && draggedTabId !== page.id && draggedTabId.startsWith('power-')) {
                    const fromIndex = powerPages.findIndex(p => p.id === draggedTabId)
                    movePowerPage(fromIndex, index)
                  }
                  setDraggedTabId(null)
                }}
                onDragEnd={() => setDraggedTabId(null)}
                className={`
                  group relative flex items-center gap-1.5 px-3 py-1 rounded-t-md text-xs font-medium
                  transition-colors cursor-pointer
                  ${currentPowerPageId === page.id && (isPowerMode || (isExportMode && exportViewMode === 'power'))
                        ? 'bg-background text-foreground border-t border-x border-red-500/30'
                        : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                  ${draggedTabId === page.id ? 'opacity-50' : ''}
                `}
              >
                <GripVertical className="h-3 w-3 opacity-0 group-hover:opacity-50 cursor-grab active:cursor-grabbing" />

                {editingPageId === page.id && editingPageType === 'power' ? (
                  <Input
                    autoFocus
                    value={editingPageName}
                    onChange={(e) => setEditingPageName(e.target.value)}
                    onBlur={finishEditingPage}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') finishEditingPage()
                      if (e.key === 'Escape') {
                        setEditingPageId(null)
                        setEditingPageName('')
                      }
                    }}
                    className="h-5 w-24 px-1 text-xs"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setCurrentPowerPageId(page.id)
                      if (isExportMode) {
                        setExportViewMode('power')
                      } else if (currentStep !== 'cabling') {
                        setCurrentStep('cabling')
                      }
                    }}
                    className="cursor-pointer"
                  >
                    {page.name}
                  </span>
                )}

                {!readOnly && (
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation()
                        startEditingPage(page.id, 'power')
                      }}
                      title="Renommer"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation()
                        duplicatePowerPage(page.id)
                      }}
                      title="Dupliquer la page"
                    >
                      <Files className="h-3 w-3" />
                    </Button>

                    {powerPages.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm(`Supprimer "${page.name}" ?`)) {
                            deletePowerPage(page.id)
                          }
                        }}
                        title="Supprimer"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}

            {!readOnly && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 flex-shrink-0"
                onClick={addPowerPage}
                title="Ajouter une page Power"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Zoom et indicateurs à droite */}
      <div className="flex items-center justify-end min-w-[200px]">
        <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
          <span>{t('synoptic.zoom')}: {Math.round((10000 / viewBox.width) / 8.73 * 100)}%</span>
          {selectedElements.length > 0 && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <span>{t('synoptic.selected')}: {getEffectiveSelectionCount()}</span>
            </>
          )}
          {clipboard && clipboard.elements.length > 0 && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <Button
                variant="ghost"
                size="sm"
                className="h-5 px-2 text-xs gap-1"
                onClick={pasteElements}
                title={t('title.pasteElements').replace('{count}', String(clipboard.elements.length))}
              >
                <Copy className="h-3 w-3" />
                <span className="text-primary font-medium">{clipboard.elements.length}</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
